import { OnDestroy, Component, signal } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { isBoolean } from 'lodash-es';
import { catchError, filter, map, Observable, of, Subject, Subscription, switchMap, withLatestFrom } from 'rxjs';
import { TimeDeltaComponent } from '../time-delta/time-delta.component';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
  selector: 'app-user-login',
  templateUrl: './user-login.component.html',
  styleUrls: ['./user-login.component.scss'],
  imports: [ReactiveFormsModule, NgbModalModule, TimeDeltaComponent, RouterLink],
  standalone: true
})
export class UserLoginComponent<T> implements OnDestroy {
  @ViewChild('otpInput', { static: false })
  set otpInput(otpInput: ElementRef<HTMLInputElement>) {
    if (otpInput) this.focusMonitor.focusVia(otpInput.nativeElement, 'program');
  }
  readonly submit = new Subject<void>();
  readonly step = signal(1);
  readonly form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
    otp: new FormControl('')
  });
  readonly MessageType = MessageType; // expose enum to template
  loginStep1: (name: string, password: string) => Observable<LoginStepResult<T>> = () => of({ what: WhatDo.Error });
  loginStep2: (otp: string) => Observable<T | null> = () => of(null);
  actions: IMessageLink[] = [];
  readonly error = signal(false);
  readonly #sub: Subscription;
  readonly messages = signal<IMessage[] | null>(null);
  readonly submitting = signal(false);

  constructor(
    private readonly modal: NgbActiveModal,
    router: Router,
    private readonly focusMonitor: FocusMonitor
  ) {
    this.#sub = this.submit
      .pipe(
        withLatestFrom(this.form.valueChanges),
        switchMap(([_, a]) => {
          this.submitting.set(true);
          if (this.step() === 1) {
            return this.loginStep1(a.name ?? '', a.password ?? '').pipe(
              map(result => {
                this.messages.set(result.messages ?? null);
                switch (result.what) {
                  case WhatDo.Skip:
                    return { success: null, step: 1, result: result.skipPayload ?? null };
                  case WhatDo.Next:
                    return { success: true, step: 1, result: null as T | null };
                  case WhatDo.Error:
                    return { success: false, step: 1, result: null as T | null };
                }
              }),
              catchError(() => of({ success: false, step: 1, result: null as T | null }))
            );
          } else {
            return this.loginStep2(a.otp ?? '').pipe(
              map(result => ({ success: !!result, step: 2, result })),
              catchError(() => of({ success: false, step: 2, result: null as T | null }))
            );
          }
        })
      )
      .subscribe(({ success, step, result }) => {
        if (step === 1) {
          if (isBoolean(success)) {
            if (success) {
              this.step.set(2);
              this.error.set(false);
              this.form.controls.otp.setValidators([Validators.required]);
              this.form.controls.otp.updateValueAndValidity();
            } else {
              this.error.set(true);
            }
          } else {
            modal.close(result);
          }
        } else {
          if (success) {
            modal.close(result);
          } else {
            this.error.set(true);
          }
        }
        this.submitting.set(false);
      });
    this.#sub.add(
      this.form.valueChanges.subscribe(() => {
        this.error.set(false);
      })
    );
    this.#sub.add(router.events.pipe(filter(evt => evt instanceof NavigationStart)).subscribe(() => this.modal.dismiss('nav')));
  }
  close() {
    this.modal.dismiss();
  }
  isMessageLink(message: IMessage): message is IMessageLink {
    return message.type === MessageType.Link;
  }
  isMessageTimeout(message: IMessage): message is IMessageTimeout {
    return message.type === MessageType.Timeout;
  }
  ngOnDestroy() {
    this.#sub.unsubscribe();
    this.submit.complete();
    if (this.otpInput) this.focusMonitor.stopMonitoring(this.otpInput.nativeElement);
  }
}

export enum WhatDo {
  Skip,
  Next,
  Error
}
export interface LoginStepResult<T> {
  what: WhatDo;
  messages?: IMessage[];
  skipPayload?: T;
}
export enum MessageType {
  Info = 0,
  Warning = 1,
  Error = 2,
  Timeout = 3,
  Link = 4
}
export interface IMessage {
  type: MessageType;
  text: string;
}
export interface IMessageTimeout extends IMessage {
  type: MessageType.Timeout;
  date: string | Date;
}

export interface IMessageLink extends IMessage {
  type: MessageType.Link;
  url: string;
}
